import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { SafetyTestService } from '../../../../../core/data-services/safety-test.service';
import { TestAttemptService } from '../../../../../core/data-services/test-attempt.service';
import { TEST_TIME_EXPIRED } from '../../../../../core/utils/safety-test-utils';
import { SafetyTest } from '../../../../../core/data-models/safety-test.model';
import { TestQuestion } from '../../../../../core/response-models/test-question.model';
import { TestAttempt } from '../../../../../core/data-models/test-attempt.model';
import { TestResultSummary } from '../../../../../core/response-models/test-result-summary.model';

@Component({
  selector: 'app-safety-test-take',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './safety-test-take.component.html',
  styleUrls: ['./safety-test-take.component.scss']
})
export class SafetyTestTakeComponent implements OnInit, OnDestroy {
  private safetyTestService = inject(SafetyTestService);
  private testAttemptService = inject(TestAttemptService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  testId: string | null = null;
  test: SafetyTest | null = null;
  attempt: TestAttempt | null = null;
  questions: TestQuestion[] = [];
  isLoading = false;
  error: string | null = null;

  // Expose Math for template
  Math = Math;

  // Test state
  testStarted = false;
  testCompleted = false;
  currentQuestionIndex = 0;
  userAnswers: Map<string, string> = new Map(); // questionId -> optionId
  flaggedQuestions: Set<number> = new Set();

  // Timer
  timeRemaining = 0; // in seconds
  timerInterval: any = null;

  // Results
  results: TestResultSummary | null = null;

  // Confirmation modal
  showSubmitConfirm = false;
  showExitConfirm = false;

  // Was this a resumed attempt?
  wasResumed = false;

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('testId');
    if (this.testId) {
      this.loadTest(this.testId);
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private async loadTest(testId: string): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      // Fetch test details
      this.test = await this.safetyTestService.fetchTestById(testId);

      if (!this.test) {
        this.error = 'Test not found';
        return;
      }

      // Initialize time from test settings
      this.timeRemaining = (this.test.timeLimit || 60) * 60;
    } catch (err: any) {
      this.error = err.message || 'Failed to load test';
    } finally {
      this.isLoading = false;
    }
  }

  // Timer methods
  startTimer(): void {
    this.timerInterval = setInterval(() => {
      // Update from server time if available
      if (this.attempt?.timeLimitExpiresAt) {
        const expiresAt = new Date(this.attempt.timeLimitExpiresAt);
        const now = new Date();
        this.timeRemaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
      } else if (this.timeRemaining > 0) {
        this.timeRemaining--;
      }

      if (this.timeRemaining <= 0) {
        this.submitTest();
      }
    }, 1000);
  }

  stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  get formattedTime(): string {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  get timeWarning(): boolean {
    return this.timeRemaining <= 300; // 5 minutes warning
  }

  get timeCritical(): boolean {
    return this.timeRemaining <= 60; // 1 minute critical
  }

  // Test navigation
  async startTest(): Promise<void> {
    if (!this.testId) return;

    this.isLoading = true;
    this.error = null;

    try {
      const response = await this.testAttemptService.startTestAttempt(this.testId);

      if (response) {
        this.attempt = response.attempt;
        this.questions = response.questions;
        this.wasResumed = response.resumed;

        // Restore answers if resuming
        if (response.resumed && this.attempt.answers) {
          for (const answer of this.attempt.answers) {
            this.userAnswers.set(answer.questionId, answer.selectedOptionId);
          }
          // Start at the last answered question
          this.currentQuestionIndex = Math.min(
            this.attempt.currentQuestionIndex,
            this.questions.length - 1
          );
        }

        // Update timer from server time
        if (this.attempt.timeLimitExpiresAt) {
          const expiresAt = new Date(this.attempt.timeLimitExpiresAt);
          const now = new Date();
          this.timeRemaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
        }

        this.testStarted = true;
        this.startTimer();
      } else {
        this.error = this.testAttemptService.error() || 'Failed to start test';
      }
    } catch (err: any) {
      this.error = err.message || 'Failed to start test';
    } finally {
      this.isLoading = false;
    }
  }

  get currentQuestion(): TestQuestion | null {
    if (!this.questions.length) return null;
    return this.questions[this.currentQuestionIndex];
  }

  get totalQuestions(): number {
    return this.questions.length;
  }

  async selectAnswer(optionId: string): Promise<void> {
    if (!this.attempt || !this.currentQuestion) return;

    const questionId = this.currentQuestion.id;
    this.userAnswers.set(questionId, optionId);

    // Save to server. Once the hub says time is up, grade what was saved instead of losing the attempt.
    const saved = await this.testAttemptService.saveAnswer(this.attempt.id, questionId, optionId);
    if (!saved && this.testAttemptService.errorCode() === TEST_TIME_EXPIRED) {
      this.timeRemaining = 0;
      await this.submitTest();
    }
  }

  getSelectedAnswer(): string | null {
    if (!this.currentQuestion) return null;
    return this.userAnswers.get(this.currentQuestion.id) || null;
  }

  nextQuestion(): void {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.currentQuestionIndex++;
    }
  }

  previousQuestion(): void {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    }
  }

  goToQuestion(index: number): void {
    this.currentQuestionIndex = index;
  }

  toggleFlag(): void {
    if (this.flaggedQuestions.has(this.currentQuestionIndex)) {
      this.flaggedQuestions.delete(this.currentQuestionIndex);
    } else {
      this.flaggedQuestions.add(this.currentQuestionIndex);
    }
  }

  isQuestionFlagged(index: number): boolean {
    return this.flaggedQuestions.has(index);
  }

  isQuestionAnswered(index: number): boolean {
    if (!this.questions[index]) return false;
    return this.userAnswers.has(this.questions[index].id);
  }

  get answeredCount(): number {
    return this.userAnswers.size;
  }

  get unansweredCount(): number {
    return this.questions.length - this.userAnswers.size;
  }

  get progressPercentage(): number {
    if (!this.questions.length) return 0;
    return Math.round((this.answeredCount / this.questions.length) * 100);
  }

  // Submit test
  confirmSubmit(): void {
    this.showSubmitConfirm = true;
  }

  cancelSubmit(): void {
    this.showSubmitConfirm = false;
  }

  async submitTest(): Promise<void> {
    this.stopTimer();
    this.showSubmitConfirm = false;

    if (!this.attempt) return;

    this.isLoading = true;

    try {
      const result = await this.testAttemptService.submitTest(this.attempt.id);

      if (result) {
        this.results = result;
        this.testCompleted = true;
      } else {
        this.error = this.testAttemptService.error() || 'Failed to submit test';
      }
    } catch (err: any) {
      this.error = err.message || 'Failed to submit test';
    } finally {
      this.isLoading = false;
    }
  }

  // Exit handling
  confirmExit(): void {
    this.showExitConfirm = true;
  }

  cancelExit(): void {
    this.showExitConfirm = false;
  }

  exitTest(): void {
    this.stopTimer();
    this.router.navigate(['/logbook/testing/safety']);
  }

  // View results
  viewReport(): void {
    if (this.attempt) {
      this.router.navigate(['/logbook/testing/safety', this.testId, 'report'], {
        queryParams: { attemptId: this.attempt.id }
      });
    }
  }

  async retakeTest(): Promise<void> {
    // Reset local state
    this.testStarted = false;
    this.testCompleted = false;
    this.currentQuestionIndex = 0;
    this.userAnswers.clear();
    this.flaggedQuestions.clear();
    this.results = null;
    this.attempt = null;
    this.questions = [];
    this.timeRemaining = (this.test?.timeLimit || 60) * 60;
    this.wasResumed = false;
  }

  goBack(): void {
    this.router.navigate(['/logbook/testing/safety']);
  }
}
